import { DndContext, useDraggable } from '@dnd-kit/core'
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers'
import { addHours } from 'date-fns'
import { FC, ReactNode, useContext, useMemo } from 'react'
import {
  LuChevronFirst,
  LuChevronLast,
  LuChevronLeft,
  LuChevronRight,
  LuChevronsLeft,
  LuChevronsRight,
  LuGripHorizontal,
} from 'react-icons/lu'

import { formatDateTime } from '@/lib/utils/date-time'

import { ReplayContext } from '../../contexts/ReplayContext'
import {
  MarkerPointProps,
  MarkerTrackProps,
  ReplayTimeRangeProps,
  TimeRangeSelectorProps,
} from './ReplayTimeRange.types'
import {
  buildSpeedGraphSegments,
  getPointPosition,
  getSelectorBounds,
} from './ReplayTimeRange.utils'
import { useReplayTimeRange } from './useReplayTimeRange'

import styles from './ReplayTimeRange.module.css'

export const TimeRangeSelector = (props: TimeRangeSelectorProps) => {
  const startRangeDragger = useDraggable({
    id: 'start-range-dragger',
  })
  const endRangeDragger = useDraggable({
    id: 'end-range-dragger',
  })
  const completeRangeDragger = useDraggable({
    id: 'complete-range-dragger',
  })

  const { selectionStartTime, selectionEndTime } = props
  const { leftOffset, leftClamp, leftRightClamp, rightOffset, rightClamp, rightLeftClamp } =
    getSelectorBounds(props)

  return (
    <div
      className={styles.timeSelector}
      ref={completeRangeDragger.setNodeRef}
      data-testid="replay-time-selector"
      style={{
        left: `clamp(${startRangeDragger.isDragging ? leftClamp : 0}cqw, calc(${leftOffset}cqw + ${startRangeDragger.transform?.x ?? 0}px), ${leftRightClamp}cqw)`,
        right: `clamp(${endRangeDragger.isDragging ? rightClamp : 0}cqw, calc(${rightOffset}cqw - ${endRangeDragger.transform?.x ?? 0}px), ${rightLeftClamp}cqw)`,
        transform: `translateX(clamp(${-leftOffset}cqw, ${completeRangeDragger.transform?.x ?? 0}px, ${rightOffset}cqw))`,
        cursor: completeRangeDragger.isDragging ? 'grabbing' : 'grab',
      }}
    >
      <div
        className={styles.timeDragBar}
        {...completeRangeDragger.attributes}
        {...completeRangeDragger.listeners}
        style={{ bottom: 0 }}
        data-testid="replay-complete-range-dragger"
      >
        <span className={styles.timeDragBarIcons}>
          {leftOffset < 1 ? (
            <LuChevronFirst color="#777" size={14} />
          ) : (
            <LuChevronsLeft color="#777" />
          )}

          <LuGripHorizontal color="#777" />
          {rightOffset < 1 ? (
            <LuChevronLast color="#777" size={14} />
          ) : (
            <LuChevronsRight color="#777" />
          )}
        </span>
      </div>
      <div
        className={styles.timeSelectorStart}
        ref={startRangeDragger.setNodeRef}
        {...startRangeDragger.attributes}
        {...startRangeDragger.listeners}
        title={formatDateTime(selectionStartTime)}
        data-testid="replay-start-range-dragger"
      >
        {leftOffset < 1 ? <LuChevronFirst /> : <LuChevronLeft />}
      </div>
      <div
        className={styles.timeSelectorEnd}
        ref={endRangeDragger.setNodeRef}
        {...endRangeDragger.attributes}
        {...endRangeDragger.listeners}
        title={formatDateTime(selectionEndTime)}
        data-testid="replay-end-range-dragger"
      >
        {rightOffset < 1 ? <LuChevronLast /> : <LuChevronRight />}
      </div>
    </div>
  )
}

const DefaultMarkerPoint = ({ point, track, position, ...rest }: MarkerPointProps) => {
  const ChildComponent = point.childComponent ?? track.pointChildComponent
  const { setHoveredTimestamp } = useContext(ReplayContext)

  return (
    <div
      className={[styles.eventPoint, point.className, track.pointClassName]
        .filter((v) => v)
        .join(' ')}
      title={formatDateTime(point.when)}
      style={{
        backgroundColor: point.pointColour ?? track.pointColour ?? 'transparent',
        ...track.pointStyle,
        ...point.style,
        transform: `translateX(calc(${position * 100}cqw - 50%))`,
      }}
      onMouseEnter={() => setHoveredTimestamp(point.when.getTime())}
      onMouseLeave={() => setHoveredTimestamp(null)}
      data-testid="replay-event-point"
    >
      {ChildComponent ? (
        <ChildComponent point={point} track={track} position={position} {...rest} />
      ) : null}
    </div>
  )
}

const DefaultMarkerTrack = ({
  track,
  rangeStartTime,
  rangeEndTime,
  periodInSec,
}: MarkerTrackProps) => {
  return (
    <div
      className={[styles.eventTrack, track.trackClassName].filter((v) => v).join(' ')}
      style={track.trackStyle}
    >
      {track.points.map((point, j) => {
        const MarkerPointComponent =
          point.pointComponent ?? track.pointComponent ?? DefaultMarkerPoint

        return (
          <MarkerPointComponent
            point={point}
            track={track}
            rangeStartTime={rangeStartTime}
            rangeEndTime={rangeEndTime}
            periodInSec={periodInSec}
            position={getPointPosition(point.when, rangeStartTime, periodInSec)}
            key={j}
          />
        )
      })}
    </div>
  )
}

export const SpeedLineGraphTrack: FC<MarkerTrackProps> = ({
  track,
  rangeStartTime,
  periodInSec,
}) => {
  const segments = useMemo(
    () => buildSpeedGraphSegments(track.points, rangeStartTime, periodInSec),
    [track.points, rangeStartTime, periodInSec],
  )

  if (track.points.length === 0) return null

  return (
    <div
      className={[styles.eventTrack, track.trackClassName].filter((v) => v).join(' ')}
      style={{ ...track.trackStyle, padding: 0, position: 'relative' }}
    >
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100cqw',
          height: '100%',
        }}
      >
        {segments.map((seg, i) => (
          <line
            key={i}
            x1={seg.x1}
            y1={seg.y1}
            x2={seg.x2}
            y2={seg.y2}
            stroke={seg.color}
            strokeWidth={5}
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        ))}
      </svg>
    </div>
  )
}

export const ReplayTimeRange = ({ markerTracks, ...timeProps }: ReplayTimeRangeProps) => {
  const { rangeStartTime, rangeEndTime, selectionStartTime, selectionEndTime } = timeProps
  const { rangeElementRef, periodInSec, markers, handleDragEnd } = useReplayTimeRange(timeProps)

  const { dateTimeLabels, timelineMarks } = useMemo(
    () =>
      markers.reduce(
        (acc, kind, currentIndex) => {
          acc.timelineMarks.push(
            <div
              key={currentIndex}
              className={kind === 'day' ? styles.dayMarker : styles.hourMarker}
            />,
          )
          if (kind === 'day') {
            const dayTime = addHours(rangeStartTime, currentIndex)
            acc.dateTimeLabels.push(
              <span
                key={currentIndex}
                className={styles.date}
                style={{ left: (currentIndex / (markers.length - 1)) * 100 + '%' }}
              >
                {formatDateTime(dayTime)}
              </span>,
            )
          }
          return acc
        },
        { dateTimeLabels: [] as ReactNode[], timelineMarks: [] as ReactNode[] },
      ),
    [markers, rangeStartTime],
  )

  return (
    <div className={styles.replayTimeRange} data-testid="replay-time-range">
      <div className={styles.start}>{formatDateTime(rangeStartTime)}</div>
      <div className={styles.end}>{formatDateTime(rangeEndTime)}</div>
      <div className={styles.timeline} ref={rangeElementRef}>
        <div className={styles.timelineMarkers}>{timelineMarks}</div>
        <div className={styles.eventTracks}>
          {markerTracks.map((track, i) => {
            const MarkerTrackComponent = track.trackComponent ?? DefaultMarkerTrack
            return (
              <MarkerTrackComponent
                track={track}
                key={i}
                rangeStartTime={rangeStartTime}
                rangeEndTime={rangeEndTime}
                periodInSec={periodInSec}
              />
            )
          })}
        </div>
        <DndContext onDragEnd={handleDragEnd} modifiers={[restrictToHorizontalAxis]}>
          <TimeRangeSelector
            selectionStartTime={selectionStartTime}
            selectionEndTime={selectionEndTime}
            rangeStartTime={rangeStartTime}
            rangeEndTime={rangeEndTime}
            periodInSec={periodInSec}
          />
        </DndContext>
      </div>
      <div className={styles.dayTimes}>{dateTimeLabels}</div>
    </div>
  )
}
